import tailwindColors from 'tailwindcss/colors';

export type ColorBrightness =
  | '100'
  | '200'
  | '300'
  | '400'
  | '500'
  | '600'
  | '700'
  | '800';

// This is not the Layout Theme, but the Theme for bright or dark Colors
export type ColorBrightnessTheme = 'light' | 'dark';

export const lightBrightnessValues: ColorBrightness[] = [
  '100',
  '200',
  '300',
  '400',
];

export const darkBrightnessValues: ColorBrightness[] = [
  '500',
  '600',
  '700',
  '800',
];

export const lightBackgroundColors = [
  'sky',
  'cyan',
  'amber',
  'red',
  'purple',
  'rose',
  'fuchsia',
];

export const backgroundColors = [
  ...lightBackgroundColors,
  'indigo',
  'teal',
  'blue',
];

export const getForeground = (hexcolor: string) => {
  if (!hexcolor) {
    return;
  }
  const r = Number.parseInt(hexcolor.substring(1, 3), 16);
  const g = Number.parseInt(hexcolor.substring(3, 5), 16);
  const b = Number.parseInt(hexcolor.substring(5, 7), 16);

  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  return yiq >= 128 ? 'black' : 'white';
};

export const colors = Object.keys(tailwindColors)
  .filter((color) => {
    /* The Filtering must happen before actually using the tailwindColors 
      as the result of the next map
      TODO: Remove this, when tailwind updated to a version
      where these colors are defunct, instead of deprecated */
    const deprecatedColorsMap: Record<string, number> = {
      lightBlue: 1,
      warmGray: 1,
      trueGray: 1,
      coolGray: 1,
      blueGray: 1,
    };
    return !deprecatedColorsMap[color];
  })
  .map((color) => {
    const hexColor =
      tailwindColors[color as keyof typeof tailwindColors]['500'];
    return {
      name: color,
      color: hexColor,
      textColor: getForeground(hexColor),
    };
  })
  .filter(
    (mappedColor) =>
      mappedColor.color && !/[A-Z]|gray|zinc|neutral/g.test(mappedColor.name)
  );

export const getHexByColorBrightness = (
  name: string,
  brightness: ColorBrightness
): string => {
  return tailwindColors[name as keyof typeof tailwindColors][brightness];
};

export const resolveColorByName = (name: string) => {
  return colors.find((color) => color.name === name);
};

export const resolveColorByHex = (hexcolor: string) => {
  return colors.find((color) => color.color === hexcolor);
};

export const getRandomBackgroundColor = (
  brightnessTheme?: ColorBrightnessTheme
) => {
  const colors =
    brightnessTheme === 'light' ? lightBackgroundColors : backgroundColors;
  const brightnessValues = getBrightnessValues(brightnessTheme);

  const randomColor = colors[Math.floor(Math.random() * colors.length)];
  const randomBrightness =
    brightnessValues[Math.floor(Math.random() * brightnessValues.length)];

  return `${randomColor}-${randomBrightness}`;
};

export const getBackgroundColorByIndex = (index: number) => {
  const color = backgroundColors[index % backgroundColors.length];
  const brightness = darkBrightnessValues[index % darkBrightnessValues.length];

  if(color && brightness) {
    return `${color}-${brightness}`;
  }

  return "gray-200";
};

export const getBrightnessValues = (
  brightnessTheme?: ColorBrightnessTheme
): string[] => {
  if (!brightnessTheme) {
    return [...lightBrightnessValues, ...darkBrightnessValues];
  }

  if (brightnessTheme === 'light') {
    return lightBrightnessValues;
  }

  return darkBrightnessValues;
};
